import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender
} from '@tanstack/react-table';
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

interface Guest {
  id: string;
  name: string;
  email: string;
  image: string;
  totalBookings: number;
  totalSpent: number;
  lastBooking: string;
  rating: number;
  status: 'active' | 'completed' | 'cancelled';
}

const mockGuests: Guest[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    image: 'https://picsum.photos/200/200?random=1',
    totalBookings: 8,
    totalSpent: 4250,
    lastBooking: '2024-03-15',
    rating: 4.8,
    status: 'active'
  },
  {
    id: '2',
    name: 'Michael Chen',
    email: 'mchen@example.com',
    image: 'https://picsum.photos/200/200?random=2',
    totalBookings: 5,
    totalSpent: 3800,
    lastBooking: '2024-03-10',
    rating: 4.9,
    status: 'completed'
  },
  {
    id: '3',
    name: 'Emma Davis',
    email: 'emma.d@example.com',
    image: 'https://picsum.photos/200/200?random=3',
    totalBookings: 3,
    totalSpent: 2100,
    lastBooking: '2024-03-05',
    rating: 4.7,
    status: 'active'
  }
];

const columnHelper = createColumnHelper<Guest>();

const GuestsPage: React.FC = () => {
  const [globalFilter, setGlobalFilter] = useState('');

  const columns = [
    columnHelper.accessor('name', {
      cell: info => (
        <div className="flex items-center">
          <img
            src={info.row.original.image}
            alt={info.getValue()}
            className="h-10 w-10 rounded-full object-cover mr-3"
          />
          <div>
            <div className="font-medium text-gray-900">{info.getValue()}</div>
            <div className="text-sm text-gray-500">{info.row.original.email}</div>
          </div>
        </div>
      ),
      header: 'Guest'
    }),
    columnHelper.accessor('totalBookings', {
      cell: info => <span className="text-gray-900">{info.getValue()}</span>,
      header: 'Bookings'
    }),
    columnHelper.accessor('totalSpent', {
      cell: info => (
        <span className="text-gray-900">${info.getValue().toLocaleString()}</span>
      ),
      header: 'Total Spent'
    }),
    columnHelper.accessor('rating', {
      cell: info => (
        <div className="flex items-center">
          <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
          <span className="text-gray-900">{info.getValue()}</span>
        </div>
      ),
      header: 'Rating'
    }),
    columnHelper.accessor('status', {
      cell: info => (
        <span className={`
          px-3 py-1 rounded-full text-sm font-medium
          ${info.getValue() === 'active' ? 'bg-green-100 text-green-800' : ''}
          ${info.getValue() === 'completed' ? 'bg-blue-100 text-blue-800' : ''}
          ${info.getValue() === 'cancelled' ? 'bg-red-100 text-red-800' : ''}
        `}>
          {info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1)}
        </span>
      ),
      header: 'Status'
    }),
    columnHelper.accessor('lastBooking', {
      cell: info => <span className="text-gray-500">{info.getValue()}</span>,
      header: 'Last Booking'
    })
  ];

  const table = useReactTable({
    data: mockGuests,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter
    },
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="p-8 max-w-7xl mx-auto"
    >
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Guests</h1>
          <p className="text-gray-500 mt-1">Manage and track your property guests</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder="Search guests..."
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
            />
          </div>
          <button className="bg-airbnb-red hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200">
            Export Data
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex items-center">
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <span className="text-sm text-gray-700">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GuestsPage;